import axiosInstance from '../../../config/axios';

export type PlanTier = 'basis' | 'pro' | 'premium';
export type BillingStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete';
export type BillingState = 'trialing' | 'active' | 'billing_required' | 'none';
export type BillingInterval = 'monthly' | 'yearly';
export type AddOnBillingType = 'per_location' | 'per_tenant';
export type InvoiceStatus = 'paid' | 'open' | 'draft' | 'void' | 'uncollectible';

export interface CatalogPlan {
  tier: PlanTier;
  name: string;
  price_monthly: string;
  included_employees_per_location: number;
  features: (string | { name: string })[];
}

export interface CatalogAddOn {
  slug: string;
  name: string;
  description: string;
  price_monthly: string;
  billing_type: AddOnBillingType;
  included_in_tiers: PlanTier[];
  included_payroll_runs?: number;
}

export interface CatalogResponse {
  plans: CatalogPlan[];
  addons: CatalogAddOn[];
  extra_employee_pack: { size: number; price_monthly: string };
  extra_payroll_pack?: { size: number; price_monthly: string };
  yearly_available: boolean;
}

export interface SummarySubscription {
  status: BillingStatus;
  plan: {
    tier: PlanTier;
    name: string;
    price_per_location: string;
    included_employees_per_location: number;
  };
  billing_interval: BillingInterval;
  current_period_end: string;
  trial_end?: string | null;
  cancel_at_period_end: boolean;
}

export interface SummaryUsage {
  locations_count: number;
  subscribed_locations: number;
  active_employees_count: number;
  max_employees: number;
  employees_utilization_percent: number;
}

export interface SummaryCosts {
  plan_subtotal: string;
  extra_employees_subtotal: string;
  addons_subtotal: string;
  total_monthly: string;
}

export interface SummaryResponse {
  has_subscription: boolean;
  subscription?: SummarySubscription;
  usage: SummaryUsage;
  costs: SummaryCosts;
  addons: string[];
  extra_employee_packs: number;
  billing_state?: BillingState;
  is_design_partner?: boolean;
  trial_days_remaining?: number;
}

export interface EstimateRequest {
  tier: PlanTier;
  extra_employee_packs: number;
  addon_slugs: string[];
  interval: BillingInterval;
  locations?: number;
}

export interface EstimateAddonLine {
  slug: string;
  name: string;
  billing_type: AddOnBillingType;
  price_per_unit: string;
  quantity: number;
  subtotal: string;
  included_in_plan: boolean;
}

export interface EstimateResponse {
  plan: {
    tier: PlanTier;
    name: string;
    price_per_location: string;
    locations: number;
    subtotal: string;
  };
  extra_employees: {
    packs: number;
    subtotal: string;
    total_extra_employees: number;
  };
  addons: EstimateAddonLine[];
  total_monthly: string;
  total_employees_included: number;
  warnings: string[];
  blockers: string[];
  yearly_available: boolean;
}

export interface BillingAccount {
  billing_email: string;
  legal_name: string;
  street: string;
  house_no: string;
  postal_code: string;
  city: string;
  country: string;
  vat_id: string;
  signatory_name: string;
  phone: string;
}

export interface BillingInvoice {
  stripe_invoice_id: string;
  invoice_number: string;
  amount_due: string;
  status: InvoiceStatus;
  period_start?: string;
  period_end?: string;
  pdf_url?: string | null;
  hosted_invoice_url?: string | null;
}

export interface CheckoutRequest {
  tier: PlanTier;
  success_url: string;
  cancel_url: string;
  addon_slugs: string[];
  extra_employee_packs: number;
  interval: BillingInterval;
}

export function parseDecimal(value: string | number): number {
  if (typeof value === 'number') return value;
  return parseFloat(value) || 0;
}

export default class BillingService {
  static async getCatalog(): Promise<CatalogResponse> {
    const { data } = await axiosInstance.get('/api/admin/billing/catalog/');
    return data;
  }

  static async getSummary(): Promise<SummaryResponse> {
    const { data } = await axiosInstance.get('/api/admin/billing/summary/');
    return data;
  }

  static async getEstimate(req: EstimateRequest): Promise<EstimateResponse> {
    const { data } = await axiosInstance.post('/api/admin/billing/estimate/', req);
    return data;
  }

  static async getAccount(): Promise<BillingAccount> {
    const { data } = await axiosInstance.get('/api/admin/billing/account/');
    return data;
  }

  static async updateAccount(account: Partial<BillingAccount>): Promise<BillingAccount> {
    const { data } = await axiosInstance.put('/api/admin/billing/account/', account);
    return data;
  }

  static async getInvoices(): Promise<BillingInvoice[]> {
    const { data } = await axiosInstance.get('/api/admin/billing/invoices/');
    return data.invoices ?? data;
  }

  static async checkout(req: CheckoutRequest): Promise<{ checkout_url: string }> {
    const { data } = await axiosInstance.post('/api/admin/billing/checkout/', req);
    return data;
  }

  static async fulfillCheckout(sessionId: string): Promise<{ status: string }> {
    const { data } = await axiosInstance.post('/api/admin/billing/fulfill-checkout/', { session_id: sessionId });
    return data;
  }

  static async getBillingPortalUrl(returnUrl: string): Promise<{ portal_url: string }> {
    const { data } = await axiosInstance.post('/api/admin/billing/billing-portal/', { return_url: returnUrl });
    return data;
  }

  static async changePlan(
    newTier: PlanTier,
    extraEmployeePacks: number,
    addonSlugs: string[],
  ): Promise<void> {
    await axiosInstance.post('/api/admin/billing/change-plan/', {
      tier: newTier,
      extra_employee_packs: extraEmployeePacks,
      addon_slugs: addonSlugs,
    });
  }
}
